import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import CircularProgress from "@material-ui/core/CircularProgress";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemText from "@material-ui/core/ListItemText";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import EditIcon from "@material-ui/icons/Edit";
import CloseIcon from "@material-ui/icons/Close";
import EmailIcon from "@material-ui/icons/Email";
import { toast } from "react-toastify";
import moment from "moment";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ForbiddenPage from "../../components/ForbiddenPage";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  getInvoiceStatusLabel,
  INVOICE_STATUS_LABELS,
  formatPaymentMethod
} from "../../constants/invoiceStatus";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  section: { marginBottom: theme.spacing(3) },
  infoCard: { padding: theme.spacing(2) },
  infoLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  infoValue: {
    fontSize: "0.95rem",
    wordBreak: "break-all",
  },
  idRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  idMono: {
    fontFamily: "monospace",
    fontSize: "0.85rem",
    wordBreak: "break-all",
  },
}));

const ASAAS_BASE = "https://www.asaas.com";

const formatBRL = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `R$ ${n.toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
};

const formatDate = (d) => (d ? moment(d).format("DD/MM/YYYY") : "—");

const StatusChip = ({ status, plan }) => {
  if (plan?.trial) {
    return (
      <Chip
        size="small"
        label="Trial"
        style={{ backgroundColor: "#fff3e0", color: "#e65100" }}
      />
    );
  }
  return status ? (
    <Chip
      size="small"
      label="Ativo"
      style={{ backgroundColor: "#e8f5e9", color: "#2e7d32" }}
    />
  ) : (
    <Chip
      size="small"
      label="Inativo"
      style={{ backgroundColor: "#ffebee", color: "#c62828" }}
    />
  );
};

const InvoiceStatusChip = ({ inv, editable, onStatusChange }) => {
  const m = getInvoiceStatusLabel(inv.status);
  const [anchorEl, setAnchorEl] = useState(null);

  if (!editable) {
    return (
      <Chip
        size="small"
        label={m.label}
        style={{ backgroundColor: m.bg, color: m.fg }}
      />
    );
  }

  // No transition restrictions: a manual invoice has no external system of
  // record (Asaas) to protect from being contradicted, so any status is
  // reachable from any other — including undoing a mistaken "paid".
  return (
    <>
      <Chip
        size="small"
        label={m.label}
        icon={<EditIcon style={{ fontSize: 16, color: m.fg }} />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        style={{ backgroundColor: m.bg, color: m.fg, cursor: "pointer" }}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {Object.keys(INVOICE_STATUS_LABELS).map((key) => {
          const isCurrent = key === inv.status;
          return (
            <MenuItem
              key={key}
              selected={isCurrent}
              onClick={() => {
                setAnchorEl(null);
                if (!isCurrent) onStatusChange(inv.id, key);
              }}
            >
              <ListItemText primary={INVOICE_STATUS_LABELS[key].label} />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

const CopyableId = ({ value, asaasUrl }) => {
  const classes = useStyles();
  if (!value) return <span className={classes.infoValue}>—</span>;
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copiado para a área de transferência.");
    } catch {
      toast.error("Falha ao copiar.");
    }
  };
  return (
    <div className={classes.idRow}>
      <span className={classes.idMono}>{value}</span>
      <Tooltip title="Copiar">
        <IconButton size="small" onClick={handleCopy}>
          <FileCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {asaasUrl && (
        <Tooltip title="Abrir no Asaas">
          <IconButton
            size="small"
            component="a"
            href={asaasUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );
};

const InfoField = ({ label, children }) => {
  const classes = useStyles();
  return (
    <Box mb={1.5}>
      <div className={classes.infoLabel}>{label}</div>
      <div className={classes.infoValue}>{children}</div>
    </Box>
  );
};

const CompanyFinanceiro = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();
  const { user } = useContext(AuthContext);

  const [company, setCompany] = useState(null);
  const [loadingCompany, setLoadingCompany] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [payModalInvoice, setPayModalInvoice] = useState(null);
  const [cashPaymentDate, setCashPaymentDate] = useState("");
  const [cashValue, setCashValue] = useState("");
  // "Confirmar no Asaas" (cash-receipt confirmation) disabled — see the
  // commented-out block below handleConfirmCashReceipt for why.
  // const [confirmingCashReceipt, setConfirmingCashReceipt] = useState(false);
  const [sendingReminderId, setSendingReminderId] = useState(null);

  const invoiceKpis = useMemo(() => {
    const sum = (arr) => arr.reduce((s, inv) => s + (Number(inv.value) || 0), 0);
    const paid    = invoices.filter(inv => inv.status === "paid");
    const overdue = invoices.filter(inv => inv.status === "overdue");
    const pending = invoices.filter(inv => inv.status === "pending");
    return {
      paid:    { count: paid.length,    total: sum(paid) },
      overdue: { count: overdue.length, total: sum(overdue) },
      pending: { count: pending.length, total: sum(pending) },
    };
  }, [invoices]);

  const fetchAll = useCallback(async () => {
    setLoadingCompany(true);
    setLoadingInvoices(true);
    try {
      const [companyRes, invoicesRes] = await Promise.all([
        api.get(`/companies/${id}`),
        api.get(`/invoices/all`, { params: { companyId: id } }),
      ]);
      setCompany(companyRes.data);
      setInvoices(invoicesRes.data?.invoices || invoicesRes.data || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingCompany(false);
      setLoadingInvoices(false);
    }
  }, [id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleUpdateInvoiceStatus = async (invoiceId, status, paidAt) => {
    try {
      const { data } = await api.put(`/invoices/${invoiceId}`, {
        status,
        paidAt,
      });
      setInvoices((prev) =>
        prev.map((inv) =>
          inv.id === invoiceId
            ? {
                ...inv,
                status,
                manualStatusChangeAt: data.manualStatusChangeAt,
                ...(status === "paid" && paidAt ? { paidAt } : {}),
              }
            : inv
        )
      );
      toast.success("Status da fatura atualizado com sucesso.");
    } catch (err) {
      toastError(err);
    }
  };

  // "Pago" gets an extra step (which date was it paid on?) before saving;
  // every other status saves immediately like before.
  const handleStatusMenuSelect = (invoiceId, status) => {
    if (status === "paid") {
      const invoice = invoices.find((inv) => inv.id === invoiceId);
      setPayModalInvoice(invoice || { id: invoiceId });
      setCashPaymentDate(moment().format("YYYY-MM-DD"));
      setCashValue(invoice?.value != null ? String(invoice.value) : "");
      return;
    }
    handleUpdateInvoiceStatus(invoiceId, status);
  };

  const closePayModal = () => {
    setPayModalInvoice(null);
    setCashPaymentDate("");
    setCashValue("");
  };

  const confirmPayModal = () => {
    handleUpdateInvoiceStatus(payModalInvoice.id, "paid", cashPaymentDate || undefined);
    closePayModal();
  };

  // Asaas-linked invoices must never have their status set by anything
  // other than Asaas's own webhook — not even indirectly, via our own call
  // to their receiveInCash API. Boss's rule (2026-08-12): a cash payment on
  // a real Asaas charge gets recorded directly in the Asaas dashboard; our
  // webhook picks it up the normal way, same as any other payment. Kept
  // here commented out, not deleted, in case this is ever revisited.
  //
  // const handleConfirmCashReceipt = async () => {
  //   setConfirmingCashReceipt(true);
  //   try {
  //     await api.post(`/invoices/${payModalInvoice.id}/receive-in-cash`, {
  //       paymentDate: cashPaymentDate,
  //       value: Number(cashValue),
  //     });
  //     toast.success(
  //       "Confirmado no Asaas! A atualização do status pode levar alguns instantes — atualize a página para ver o resultado."
  //     );
  //     closePayModal();
  //   } catch (err) {
  //     toastError(err);
  //   } finally {
  //     setConfirmingCashReceipt(false);
  //   }
  // };

  const handleSendReminder = async (invoiceId) => {
    setSendingReminderId(invoiceId);
    try {
      await api.post(`/invoices/${invoiceId}/send-reminder`);
      toast.success("Lembrete enviado.");
    } catch (err) {
      toastError(err);
    } finally {
      setSendingReminderId(null);
    }
  };

  if (!user?.super) {
    return <ForbiddenPage />;
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>
          {loadingCompany ? "Carregando..." : `Empresa: ${company?.name || "—"}`}
        </Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push("/financeiro")}
          >
            Voltar
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {loadingCompany ? (
          <Box textAlign="center" py={6}>
            <CircularProgress />
          </Box>
        ) : !company ? (
          <Typography color="textSecondary">Empresa não encontrada.</Typography>
        ) : (
          <>
            {/* Visão geral da empresa */}
            <Box className={classes.section}>
              <Box display="flex" alignItems="center" mb={2} style={{ gap: 12 }}>
                <Typography variant="h6">{company.name}</Typography>
                <StatusChip status={company.status} plan={company.plan} />
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <InfoField label="E-mail">
                    {company.email || "—"}
                  </InfoField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InfoField label="Documento (CPF/CNPJ)">
                    {company.document || "—"}
                  </InfoField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InfoField label="Telefone">
                    {company.phone || "—"}
                  </InfoField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InfoField label="Plano atual">
                    {company.plan?.name || "—"}
                    {company.plan?.amount != null && (
                      <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
                        ({formatBRL(company.plan.amount)})
                      </Typography>
                    )}
                  </InfoField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InfoField label="Forma de pagamento">
                    {formatPaymentMethod(company.paymentFlow, company.paymentMethod)}
                  </InfoField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InfoField label="Próximo vencimento">
                    {formatDate(company.dueDate)}
                  </InfoField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InfoField label="Recorrência">
                    {(company.plan?.recurrence || company.recurrence || "—").toString().toUpperCase()}
                  </InfoField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InfoField label="Último login">
                    {company.lastLogin ? moment(company.lastLogin).format("DD/MM/YYYY HH:mm") : "—"}
                  </InfoField>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InfoField label="Cadastrada em">
                    {formatDate(company.createdAt)}
                  </InfoField>
                </Grid>
              </Grid>
            </Box>

            {/* Totais de faturas */}
            <Box className={classes.section}>
              <Grid container spacing={2}>
                {[
                  { label: "Valor total pago",      kpi: invoiceKpis.paid,    color: "#2e7d32", bg: "#e8f5e9" },
                  { label: "Valor total pendente",   kpi: invoiceKpis.pending, color: "#9e6b00", bg: "#fff8e1" },
                  { label: "Valor total em atraso",  kpi: invoiceKpis.overdue, color: "#c62828", bg: "#ffebee" },
                ].map(({ label, kpi, color, bg }) => (
                  <Grid item xs={12} sm={4} key={label}>
                    <Paper variant="outlined" style={{ padding: "16px 20px", borderLeft: `4px solid ${color}`, background: bg }}>
                      <Typography style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.06em", color, fontWeight: 600, marginBottom: 6 }}>
                        {label}
                      </Typography>
                      <Typography style={{ fontSize: "1.5rem", fontWeight: 700, color, lineHeight: 1.1 }}>
                        {formatBRL(kpi.total)}
                      </Typography>
                      <Typography style={{ fontSize: "0.75rem", color, marginTop: 4, opacity: 0.75 }}>
                        {kpi.count} {kpi.count === 1 ? "fatura" : "faturas"}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Asaas — apenas para o super */}
            <Box className={classes.section}>
              <Typography variant="subtitle1" gutterBottom>
                Asaas
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <InfoField label="Customer ID">
                    <CopyableId
                      value={company.asaasCustomerId}
                      asaasUrl={
                        company.asaasCustomerId
                          ? `${ASAAS_BASE}/customer/${company.asaasCustomerId}`
                          : null
                      }
                    />
                  </InfoField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoField label="Subscription ID">
                    <CopyableId value={company.asaasSubscriptionId} />
                  </InfoField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoField label="Installment ID">
                    <CopyableId value={company.asaasInstallmentId} />
                  </InfoField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoField label="Installment count">
                    {company.asaasInstallmentCount ?? "—"}
                  </InfoField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoField label="Installment ativado em">
                    {company.asaasInstallmentActivatedAt
                      ? moment(company.asaasInstallmentActivatedAt).format("DD/MM/YYYY HH:mm")
                      : "—"}
                  </InfoField>
                </Grid>
              </Grid>
            </Box>

            {/* Histórico de faturas */}
            <Box className={classes.section}>
              <Typography variant="subtitle1" gutterBottom>
                Histórico de faturas
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell>Vencimento</TableCell>
                    <TableCell>Pagamento</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Alterado Manualmente</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingInvoices ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Box py={3}>
                          <CircularProgress size={24} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : invoices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Box py={3}>
                          <Typography color="textSecondary">
                            Nenhuma fatura registrada.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    invoices.map((inv) => (
                      <TableRow key={inv.id}>
                        <TableCell>{inv.id}</TableCell>
                        <TableCell>{inv.detail || "—"}</TableCell>
                        <TableCell>{formatDate(inv.dueDate)}</TableCell>
                        <TableCell>
                          {inv.status === "paid"
                            ? formatDate(inv.paidAt || inv.manualStatusChangeAt)
                            : "—"}
                        </TableCell>
                        <TableCell>
                          <InvoiceStatusChip
                            inv={inv}
                            editable={!inv.asaasPaymentId}
                            onStatusChange={handleStatusMenuSelect}
                          />
                        </TableCell>
                        <TableCell>
                          {inv.manualStatusChangeAt
                            ? `Sim - ${formatDate(inv.manualStatusChangeAt)}`
                            : "Não"}
                        </TableCell>
                        <TableCell align="right">{formatBRL(inv.value)}</TableCell>
                        <TableCell align="center">
                          {inv.status === "pending" ? (
                            <Tooltip title="Enviar lembrete de vencimento">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={sendingReminderId === inv.id}
                                  onClick={() => handleSendReminder(inv.id)}
                                >
                                  {sendingReminderId === inv.id ? (
                                    <CircularProgress size={18} />
                                  ) : (
                                    <EmailIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </Paper>

      <Dialog open={!!payModalInvoice} onClose={closePayModal} maxWidth="xs" fullWidth>
        <DialogTitle disableTypography>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between">
            <Typography variant="h6">Marcar fatura como paga</Typography>
            <IconButton size="small" onClick={closePayModal} style={{ marginTop: -4, marginRight: -8 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Typography variant="body2" color="textSecondary">
              Fatura: {payModalInvoice?.detail || "—"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Vencimento da fatura: {formatDate(payModalInvoice?.dueDate)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Valor: {formatBRL(payModalInvoice?.value)}
            </Typography>
          </Box>

          {/* Asaas-linked invoices must never have their status set by
              anything other than Asaas's own webhook — see the note above
              handleConfirmCashReceipt. Original branch kept below,
              commented out, not deleted.

          {payModalInvoice?.asaasPaymentId ? (
            <>
              <Box
                mb={2}
                p={2}
                style={{ backgroundColor: "#e5f6f4", border: "1px solid #bfe8e3", borderRadius: 6 }}
              >
                <Typography
                  variant="caption"
                  style={{ color: "#0b756c", fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase" }}
                >
                  Vinculada a uma cobrança real
                </Typography>
                <Typography variant="body2" style={{ marginTop: 6 }}>
                  Esta fatura tem uma cobrança em aberto no Asaas. Confirme o recebimento abaixo
                  para que o Asaas atualize o status, o vencimento e a liberação de acesso
                  automaticamente.
                </Typography>
                <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 6 }}>
                  {payModalInvoice.asaasPaymentId}
                </Typography>
              </Box>

              <Box display="flex" style={{ gap: 12 }} mb={2}>
                <TextField
                  label="Data do pagamento"
                  type="date"
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={cashPaymentDate}
                  onChange={(e) => setCashPaymentDate(e.target.value)}
                />
                <TextField
                  label="Valor pago"
                  type="number"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={cashValue}
                  onChange={(e) => setCashValue(e.target.value)}
                />
              </Box>
            </>
          ) : (
            <>
              <TextField
                label="Data do pagamento"
                type="date"
                fullWidth
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={cashPaymentDate}
                onChange={(e) => setCashPaymentDate(e.target.value)}
                helperText="Não altera o vencimento de acesso da empresa."
              />
            </>
          )}
          */}
          <TextField
            label="Data do pagamento"
            type="date"
            fullWidth
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={cashPaymentDate}
            onChange={(e) => setCashPaymentDate(e.target.value)}
            helperText="Não altera o vencimento de acesso da empresa."
          />
        </DialogContent>
        <DialogActions>
          {/* "Confirmar no Asaas" button disabled along with the section
              above — same reason, kept for later, not deleted.

          {payModalInvoice?.asaasPaymentId ? (
            <Button
              onClick={handleConfirmCashReceipt}
              color="primary"
              variant="contained"
              disabled={confirmingCashReceipt || !cashPaymentDate || !cashValue}
              style={{ backgroundColor: "#0e9488" }}
            >
              Confirmar no Asaas
            </Button>
          ) : (
            <Button
              onClick={confirmPayModal}
              color="primary"
              variant="contained"
              disabled={!cashPaymentDate}
            >
              Atualizar
            </Button>
          )}
          */}
          <Button
            onClick={confirmPayModal}
            color="primary"
            variant="contained"
            disabled={!cashPaymentDate}
          >
            Atualizar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default CompanyFinanceiro;
